import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Box, Chip, Typography, useMediaQuery, MenuItem, Menu, ListItemIcon, ListItemText } from '@mui/material';
import ListView from '../../SystemDesign/ListViewV2';
import DateInTable from '../../SystemDesign/DateInTable';
import { formattedDate } from '../../Utils/Functions';
import EvidenceFileIcon from '../../SystemDesign/EvidenceFileIcon';
import RoleChip from '../../SystemDesign/RoleChip';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';
import ListViewMenuActions from '../../SystemDesign/ListViewMenuActions';
import UpdatePracticleEvidence from '../Evaluations/PracticalEvidences/UpdatePracticleEvidence';
import DeletePractileEvidence from '../Evaluations/PracticalEvidences/DeletePractileEvidence';
import DeleteEvaluation from '../Evaluations/DeleteEvaluation';
import SeeMoreEvaluation from '../Evaluations/SeeMoreEvaluation';
import UpdateEvaluation from '../Evaluations/UpdateEvaluation';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../Reducers/Actions';

// Small helper to render evaluation type (original styling)
const TypeChip = ({ type }) => {
  if (!type) return null;
  return <Chip size="small" label={String(type)} color="primary" variant="outlined" />;
};

// Merge helper: overlay live evidences onto originals by id, preserving others, and removing tombstoned ids.
function mergeEvidences(originals = [], live = [], tombstones = []) {
  const byId = new Map();
  (Array.isArray(originals) ? originals : []).forEach((ev) => {
    const id = ev?.evidence_id ?? ev?.id;
    if (id != null) byId.set(String(id), ev);
  });
  (Array.isArray(live) ? live : []).forEach((ev) => {
    const id = ev?.evidence_id ?? ev?.id;
    if (id != null) byId.set(String(id), { ...byId.get(String(id)), ...ev });
  });
  // Remove any tombstoned ids (deleted items)
  const tomb = new Set((tombstones || []).map((t) => String(t)));
  if (tomb.size) {
    for (const key of Array.from(byId.keys())) {
      if (tomb.has(key)) byId.delete(key);
    }
  }
  // Include any live items without matching originals (e.g., newly added)
  const merged = Array.from(byId.values());
  // Keep a stable order: originals first, then any new lives not in originals
  const origIds = new Set((originals || []).map((ev) => String(ev?.evidence_id ?? ev?.id)));
  const newOnes = (live || []).filter((ev) => !origIds.has(String(ev?.evidence_id ?? ev?.id)) && !tomb.has(String(ev?.evidence_id ?? ev?.id)));
  return [...(originals || []).map((ev) => merged.find((m) => String(m?.evidence_id ?? m?.id) === String(ev?.evidence_id ?? ev?.id)) || ev), ...newOnes];
}

// Actions menu cell with Edit/Delete submenus listing evidences and Delete Evaluation action
const ActionsMenuCell = ({ evidences = [], evaluation, onOpenUpdate }) => {
  // Pull live evidences for this evaluation from Redux so labels update instantly
  const peSlice = useSelector((state) => state?.practicalEvidences);
  const practicalEvidences = Array.isArray(peSlice) ? peSlice : (peSlice?.items || []);
  const evidenceTombstones = Array.isArray(peSlice) ? [] : (peSlice?.tombstones || []);
  const evalId = evaluation?.evaluation_id || evaluation?.id || evaluation?.evaluationId;
  const liveEvidences = (Array.isArray(practicalEvidences) ? practicalEvidences : [])
    .filter((pe) => String(pe?.evaluationId ?? pe?.evaluation_id) === String(evalId));
  const tomb = new Set((evidenceTombstones || []).map(String));
  const mergedList = mergeEvidences(evidences, liveEvidences, evidenceTombstones)
    .filter((ev) => {
      const id = ev?.evidence_id ?? ev?.id;
      return id != null && !tomb.has(String(id));
    });
  // Dedupe by id to avoid ghost duplicates
  const byIdLocal = new Map();
  mergedList.forEach((ev) => {
    const key = String(ev?.evidence_id ?? ev?.id);
    if (!byIdLocal.has(key)) byIdLocal.set(key, ev);
  });
  const evidenceList = Array.from(byIdLocal.values());
  // Use anchorPosition to precisely place submenu to the right of the parent item
  const [editAnchorPos, setEditAnchorPos] = useState(null);
  const editTriggerRef = useRef(null);
  const openEdit = Boolean(editAnchorPos);
  const handleOpenEditClick = () => {
    const el = editTriggerRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      setEditAnchorPos({ top: Math.round(rect.top), left: Math.round(rect.right + 8) });
    } else {
      // Fallback to cursor-ish area
      setEditAnchorPos({ top: 100, left: 100 });
    }
  };
  const handleCloseEdit = () => setEditAnchorPos(null);

  // Delete submenu state
  const [deleteAnchorPos, setDeleteAnchorPos] = useState(null);
  const deleteTriggerRef = useRef(null);
  const openDelete = Boolean(deleteAnchorPos);
  const handleOpenDeleteClick = () => {
    const el = deleteTriggerRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      setDeleteAnchorPos({ top: Math.round(rect.top), left: Math.round(rect.right + 8) });
    } else {
      setDeleteAnchorPos({ top: 100, left: 100 });
    }
  };
  const handleCloseDelete = () => setDeleteAnchorPos(null);

  // If only one evidence, show a direct Edit action; if more, show nested submenu
  const hasMany = evidenceList.length > 1;

  const single = evidenceList.length === 1 ? evidenceList[0] : null;

  return (
    <>
      <ListViewMenuActions>
        {/* Always show See More, Edit, and Delete for the evaluation */}
        {evaluation ? (
          <>
            <SeeMoreEvaluation row={{ original: evaluation }} />
            {(() => {
              const resolvedId = evaluation?.evaluation_id ?? evaluation?.id ?? evaluation?.evaluationId;
              if (resolvedId == null) return null;
              return (
                <UpdateEvaluation
                  row={{
                    original: {
                      evaluationId: resolvedId,
                      id: resolvedId,
                      evaluation_id: resolvedId,
                      score: evaluation?.score,
                      comments: evaluation?.comments,
                      level: evaluation?.level,
                      evaluation_type: evaluation?.evaluation_type,
                      evaluation_date: evaluation?.evaluation_date,
                      skill_id: evaluation?.skill_id || evaluation?.skill?.id,
                      title: evaluation?.skill?.name,
                    },
                  }}
                />
              );
            })()}
            <DeleteEvaluation row={{ original: evaluation }} />
          </>
        ) : (
          <MenuItem disabled>No evaluation</MenuItem>
        )}

        {hasMany ? (
          <>
            <MenuItem onClick={handleOpenEditClick} ref={editTriggerRef}>
              <ListItemIcon>
                {UpdatePracticleEvidence.menuIcon || null}
              </ListItemIcon>
              <ListItemText>{UpdatePracticleEvidence.menuLabel}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleOpenDeleteClick} ref={deleteTriggerRef}>
              <ListItemIcon>
                {DeletePractileEvidence.menuIcon || null}
              </ListItemIcon>
              <ListItemText>{DeletePractileEvidence.menuLabel}</ListItemText>
            </MenuItem>
          </>
        ) : (
          single ? (
            <>
              <MenuItem onClick={() => onOpenUpdate && onOpenUpdate(single)}>
                <ListItemIcon>
                  {UpdatePracticleEvidence.menuIcon || null}
                </ListItemIcon>
                <ListItemText>Edit Evidence 1</ListItemText>
              </MenuItem>
              <DeletePractileEvidence row={{ original: single }} label="Delete Evidence 1" />
            </>
          ) : <></>
        )}
      </ListViewMenuActions>

      {hasMany && (
        <Menu
          anchorReference="anchorPosition"
          anchorPosition={editAnchorPos || { top: 0, left: 0 }}
          open={openEdit}
          onClose={handleCloseEdit}
          keepMounted
          disablePortal={false}
          sx={{ zIndex: 99999 }}
          ModalProps={{
            keepMounted: true,
            container: typeof document !== 'undefined' ? document.body : undefined,
            disableAutoFocus: true,
            disableEnforceFocus: true,
            disableScrollLock: true,
            BackdropProps: { invisible: true },
            sx: { zIndex: (theme) => (theme?.zIndex?.modal || 1300) + 2000 },
          }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ elevation: 16, sx: { ml: 0, minWidth: 220, py: 0.5, zIndex: 99999, pointerEvents: 'auto' } }}
       >
          {evidenceList.map((ev, idx) => (
            <MenuItem
              key={`${ev?.evidence_id || ev?.id || idx}`}
              onClick={() => {
                onOpenUpdate && onOpenUpdate(ev);
                handleCloseEdit();
              }}
            >
              <ListItemIcon>
                {UpdatePracticleEvidence.menuIcon || null}
              </ListItemIcon>
              <ListItemText>{`Edit Evidence ${idx + 1}`}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      )}

      {hasMany && (
        <Menu
          anchorReference="anchorPosition"
          anchorPosition={deleteAnchorPos || { top: 0, left: 0 }}
          open={openDelete}
          onClose={handleCloseDelete}
          keepMounted
          disablePortal={false}
          sx={{ zIndex: (theme) => (theme?.zIndex?.modal || 1300) + 3000 }}
          ModalProps={{
            keepMounted: true,
            container: typeof document !== 'undefined' ? document.body : undefined,
            disableAutoFocus: true,
            disableEnforceFocus: true,
            disableScrollLock: true,
            BackdropProps: { invisible: true },
            sx: { zIndex: (theme) => (theme?.zIndex?.modal || 1300) + 2000 },
          }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ elevation: 16, sx: { ml: 0, minWidth: 220, py: 0.5, zIndex: 99999, pointerEvents: 'auto' } }}
        >
          {evidenceList.map((ev, idx) => (
            <span key={`del-${ev?.evidence_id || ev?.id || idx}`} onClick={handleCloseDelete}>
              <DeletePractileEvidence row={{ original: ev }} label={`Delete Evidence ${idx + 1}`} />
            </span>
          ))}
        </Menu>
      )}
    </>
  );
};

function resolveBackendBase() {
  let envBase = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_BACKEND_URL)
    || process.env.REACT_APP_BACKEND_URL
    || '';
  envBase = (envBase || '').trim();
  if (!envBase) return window.location.origin;
  try {
    const url = new URL(envBase, window.location.origin);
    const origin = url.origin;
    const path = (url.pathname || '').replace(/\/$/, '');
    return `${origin}${path}`;
  } catch {
    return window.location.origin;
  }
}

async function downloadEvidenceById(evidenceId, fileName) {
  if (!evidenceId) return;
  const base = resolveBackendBase();
  const href = `${base}/skills-management/practical-evidences/${evidenceId}/download`;
  let token;
  try {
    const stored = localStorage.getItem('user');
    if (stored) token = JSON.parse(stored)?.token;
  } catch (_) {}
  try {
    const res = await fetch(href, {
      method: 'GET',
      headers: {
        Accept: 'application/octet-stream,application/pdf,image/*,video/*',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      cache: 'no-cache',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const dispo = res.headers.get('content-disposition') || '';
    let suggested = fileName || `evidence-${evidenceId}`;
    const match = dispo.match(/filename\*=UTF-8''([^;]+)|filename=\"?([^";]+)\"?/i);
    if (match) suggested = decodeURIComponent(match[1] || match[2] || suggested);
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = suggested;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    // Fallback open in new tab
    window.open(href, '_blank', 'noopener');
  }
}

/**
 * Table view for employee evaluations using ListViewV2.
 * Props:
 *  - evaluations: array
 *  - queryParams: object with {filters, sorting, pagination(JSON string), search}
 *  - setQueryParams: function
 *  - applyFilters: function(filters, paginationInfo, sortingInfo, search)
 */
const MyEvaluationList = ({ evaluations = [], queryParams, setQueryParams, applyFilters, selfEmployeeId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMdOrLarger = useMediaQuery((theme) => theme.breakpoints.up('md'));
  const isSmOrLarger = useMediaQuery((theme) => theme.breakpoints.up('sm'));

  // Redux wiring: initialize once from props, then always render from Redux
  const dispatch = useDispatch();
  const evalSlice = useSelector((state) => state?.evaluations);
  const reduxEvaluations = Array.isArray(evalSlice) ? evalSlice : (evalSlice?.items || []);
  // Also observe practical evidences so table updates instantly after edits
  const peSlice = useSelector((state) => state?.practicalEvidences);
  const practicalEvidences = Array.isArray(peSlice) ? peSlice : (peSlice?.items || []);
  const evidenceTombstones = Array.isArray(peSlice) ? [] : (peSlice?.tombstones || []);
  const initializedRef = useRef(null);
  useEffect(() => {
    if (!initializedRef.current && Array.isArray(evaluations) && evaluations.length > 0) {
      dispatch({ type: actions.INITIALIZE_EVALUATIONS, payload: evaluations });
      initializedRef.current = true;
    }
  }, [dispatch, evaluations]);

  const paginationState = (() => {
    try {
      return JSON.parse(queryParams?.pagination || '{}');
    } catch {
      return { currentPage: 1, rowsPerPage: 10 };
    }
  })();

  const searchTerm = (queryParams?.search || '').toLowerCase();
  const activeType = (() => {
    try {
      const f = queryParams?.filters ? JSON.parse(queryParams.filters) : {};
      return f.type || '';
    } catch {
      return '';
    }
  })();

  const { pageItems, total, currentPage, rowsPerPage } = useMemo(() => {
    const eid = Number(selfEmployeeId);
    const sourceRaw = Array.isArray(reduxEvaluations) && reduxEvaluations.length > 0 ? reduxEvaluations : (evaluations || []);
    const source = Number.isFinite(eid)
      ? sourceRaw.filter((e) => String(e?.employee_id ?? e?.employee?.id) === String(eid))
      : sourceRaw;
    const filtered = source.filter((e) => {
      const skillName = e?.skill?.name || '';
      const unitName = e?.skill?.unit?.name || '';
      const type = e?.evaluation_type || '';
      const comments = e?.comments || '';
      const hay = `${skillName} ${unitName} ${type} ${comments}`.toLowerCase();
      const matchesSearch = searchTerm ? hay.includes(searchTerm) : true;
      const matchesType = activeType ? String(type).toLowerCase() === String(activeType).toLowerCase() : true;
      return matchesSearch && matchesType;
    });

    // Slice locally for manualPagination mode
    const sorted = filtered;
    const cp = paginationState.currentPage || 1;
    const rpp = paginationState.rowsPerPage || 10;
    const startIndex = (cp - 1) * rpp;
    const endIndex = startIndex + rpp;
    return {
      pageItems: sorted.slice(startIndex, endIndex),
      total: sorted.length,
      currentPage: cp,
      rowsPerPage: rpp,
    };
  }, [reduxEvaluations, evaluations, queryParams?.sorting, queryParams?.filters, queryParams?.search, queryParams?.pagination, selfEmployeeId]);

  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [selectedEvidence, setSelectedEvidence] = useState(null);

  const openUpdateDialog = (evidence) => {
    setSelectedEvidence(evidence);
    setUpdateDialogOpen(true);
  };

  const columns = useMemo(() => [
    {
      id: 'skill',
      accessorKey: 'skill.name',
      header: 'Skill',
      cell: (info) => info?.row?.original?.skill?.name || '',
      enableColumnFilter: false,
    },
    // Removed Unit column as requested
    {
      id: 'type',
      accessorKey: 'evaluation_type',
      header: 'Type',
      cell: (info) => <TypeChip type={info.getValue()} />,
      enableColumnFilter: false,
    },
    {
      id: 'level',
      accessorKey: 'level',
      header: 'Level',
      cell: (info) => {
        const v = Number(info.getValue());
        if (v === 1) return 'Awareness (1)';
        if (v === 2) return 'Skilled Application (2)';
        if (v === 3) return 'Management (3)';
        return String(info.getValue() ?? '');
      },

      enableColumnFilter: false,
    },
    {
        id: 'score',
        accessorKey: 'score',
        header: 'Score',
        cell: (info) => `${Number(info.getValue() ?? 0)}/10`,
        enableColumnFilter: false,
      },
    {
      id: 'evidence',
      header: 'Practical Evidence',
      accessorFn: (row) => row?.evidences,
      cell: ({ row }) => {
        // Prefer live evidences from Redux practicalEvidences by evaluationId
        const evalId = row?.original?.evaluation_id || row?.original?.id || row?.original?.evaluationId;
        const live = (Array.isArray(practicalEvidences) ? practicalEvidences : [])
          .filter((pe) => String(pe?.evaluationId ?? pe?.evaluation_id) === String(evalId));
        const evs = mergeEvidences(
          (Array.isArray(row?.original?.evidences) ? row.original.evidences : []),
          live,
          evidenceTombstones
        ).filter((ev) => !new Set((evidenceTombstones || []).map(String)).has(String(ev?.evidence_id ?? ev?.id)));
        if (evs.length === 0) return '-';
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {evs.map((ev, idx) => {
              const name = ev?.file_name || (ev?.evidence_url ? String(ev.evidence_url).split('/').pop() : 'Evidence');
              const id = ev?.evidence_id || ev?.id;
              if (!id) return null;
              return (
                <Box key={`${id}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EvidenceFileIcon evidence={ev} title={name} onClick={() => downloadEvidenceById(id, name)} />
                </Box>
              );
            }).filter(Boolean)}
          </Box>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      id: 'evidenceStatus',
      header: 'Evidence Status',
      accessorFn: (row) => (Array.isArray(row?.evidences) && row.evidences[0]?.status) || 'Pending',
      cell: ({ row }) => {
        const evalId = row?.original?.evaluation_id || row?.original?.id || row?.original?.evaluationId;
        const live = (Array.isArray(practicalEvidences) ? practicalEvidences : [])
          .filter((pe) => String(pe?.evaluationId ?? pe?.evaluation_id) === String(evalId));
        const evs = mergeEvidences(
          (Array.isArray(row?.original?.evidences) ? row.original.evidences : []),
          live,
          evidenceTombstones
        )
          // Skip items without a usable id (keeps UI consistent with file icons column)
          .filter((ev) => Boolean(ev?.evidence_id || ev?.id))
          // Extra guard to exclude any remaining tombstoned ids
          .filter((ev) => !new Set((evidenceTombstones || []).map(String)).has(String(ev?.evidence_id ?? ev?.id)));
        if (evs.length === 0) return '-';
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {evs.map((ev, idx) => {
              const s = String(ev?.status || 'pending').toLowerCase();
              const label = s === 'approved' ? 'Approved' : (s === 'rejected' ? 'Rejected' : 'Pending');
              const colorOverride = s === 'approved' ? colors.green[700] : (s === 'rejected' ? colors.redAccent[600] : colors.Orange[400]);
              const bgColorOverride = s === 'approved' ? colors.green[100] : (s === 'rejected' ? colors.redAccent[100] : colors.Orange[100]);
              return (
                <RoleChip
                  key={`${String(ev?.evidence_id || ev?.id)}-${idx}`}
                  role={label}
                  colorOverride={colorOverride}
                  bgColorOverride={bgColorOverride}
                />
              );
            })}
          </Box>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
    },
    
    ...(isMdOrLarger ? [{
      id: 'date',
      accessorKey: 'evaluation_date',
      header: 'Date',
      cell: (info) => <DateInTable date={formattedDate(new Date(info.getValue()))} />,
      enableColumnFilter: false,
    }] : []),
    ...(isMdOrLarger ? [{
      id: 'comments',
      accessorKey: 'comments',
      header: 'Comments',
      cell: (info) => (
        <Typography variant="body2" noWrap title={info.getValue() || ''}>
          {info.getValue() || ''}
        </Typography>
      ),
      enableColumnFilter: false,
    }] : []),
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const evs = Array.isArray(row?.original?.evidences) ? row.original.evidences : [];
        return <ActionsMenuCell evidences={evs} evaluation={row?.original} onOpenUpdate={openUpdateDialog} />;
      },
      enableColumnFilter: false,
      enableSorting: false,
    },
  ], [isMdOrLarger, isSmOrLarger, practicalEvidences, evidenceTombstones]);

  const [columnFilters, setColumnFilters] = useState([]);

  const pagination = {
    currentPage,
    rowsPerPage,
    total,
    totalPages: Math.ceil(total / rowsPerPage) || 1,
  };


  return (
    <Box>
      <ListView
        dispatchData={pageItems}
        columns={columns}
        tableName={"my-evaluations"}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        applyFilters={applyFilters}
        pagination={pagination}
        setQueryParams={setQueryParams}
        queryParams={queryParams}
        showColumnManager={false}
      />
      {/* Top-level controlled Update Evidence dialog */}
      {selectedEvidence && (
        <UpdatePracticleEvidence
          noTrigger
          forceOpen={updateDialogOpen}
          row={{ original: selectedEvidence }}
          onClose={() => setUpdateDialogOpen(false)}
        />
      )}
    </Box>
  );
};

export default MyEvaluationList;
